/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { AngularContext } from 'react-app';
import { config } from '@config/pendo.config.json';
import t from 'react-translate';

import { useSelector } from 'react-redux';
import { RootState } from 'redux/schemas';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { MentorshipProgramRequest } from 'redux/schemas/models/mentorship-program-connections';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  getMentoringProgramRequest,
  getMentoringProgramRequestsByStatus,
  getMentoringRequestsByParticipantId,
  getMentorRequestCarouselIndex,
} from 'redux/selectors/mentorship-program-connections';
import {
  MENTOR_REQUESTS_PARAMS,
  MentorRequestModalSettings,
  MentorRequestParticipantType,
  MentorRequestSource,
  SingleMentorRequestStatus,
} from 'redux/schemas/models/mentor-request';
import {
  getMentorRequest,
  setMentorRequestCarouselIndex,
} from 'redux/actions/mentorship-program-connections';
import {
  getMentorResultById,
  MentorPrevNext,
  selectFindAMentorPrevNext,
  selectShouldLoadMoreMentors,
} from 'redux/selectors/mentorship-program-participant-driven';
import { searchMentors } from 'redux/actions/mentoring-program-participant-driven';
import {
  getMentoringProgramEnrollment as getEnrollment,
} from 'redux/actions/mentoring-program-enrollments';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import { css } from '@emotion/react';

import useClearUrlParams from 'athena/hooks/use-clear-url-params';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import useDiscardMentorMessage from 'athena/hooks/use-discard-mentor-message';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import MentorRequestContent from './mentor-request-content';

import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { PAGE_SIZE } from 'athena/components/mentoring-program/constants';
import useMentorAvailability from 'athena/hooks/use-mentor-availability';

const getTimelineRequests = (requests: MentorshipProgramRequest[]) => {
  const latestRequest = requests.at(-1);
  if (!latestRequest) return [];

  if (latestRequest.state === SingleMentorRequestStatus.REJECTED) {
    return [
      latestRequest,
      { ...latestRequest, state: SingleMentorRequestStatus.PENDING },
    ];
  }

  return [latestRequest];
};

const modalStyles = css`
  .close {
    top: 0 !important;
    right: 0 !important;
  }
`;

const MentorRequestModal = () => {
  const { injectServices } = useContext(AngularContext);
  const [$state, AlertMessages] = injectServices(['$state', 'AlertMessages']);
  const { source, mentorRequest: requestId, mentorId, programId } = $state.params;
  const { isMentee, isMentor } = useMentoringProgramRole();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { mentorshipProgramEnrollment: enrollmentId } = mentoringProgram || {};
  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state: RootState) => getMentoringProgramEnrollment(state, enrollmentId)
  );
  const { availability } = useMentorAvailability(mentorshipProgramEnrollment);

  const mentoringProgramRequest: MentorshipProgramRequest = useSelector(getMentoringProgramRequest(Number(requestId)));

  const participantId = isMentee
    ? mentoringProgramRequest?.mentorEnrollmentId ?? mentorId
    : mentoringProgramRequest?.menteeEnrollmentId ?? 0;
  const participantEnrollment: MentoringProgramEnrollment = useSelector((state: RootState) => (
    getMentoringProgramEnrollment(state, participantId)
  ));

  const mentorRequestContentRef = React.useRef(null);
  const lastRequestedPageRef = React.useRef<number>(0);

  const dispatch = useAppDispatch();
  const mentoringProgramRequests = useSelector(
    getMentoringProgramRequestsByStatus(SingleMentorRequestStatus.PENDING, enrollmentId)
  );
  const carouselIndex = useSelector(getMentorRequestCarouselIndex);

  const { currentMentorId, previousMentorId, nextMentorId }: MentorPrevNext = useSelector(selectFindAMentorPrevNext(mentorId));
  const mentoringProgramParticipant: MentorRequestParticipantType = useSelector(getMentorResultById(currentMentorId));
  const { shouldLoad, searchParams } = useSelector(selectShouldLoadMoreMentors(mentorId));

  const requests: MentorshipProgramRequest[] = useSelector(
    getMentoringRequestsByParticipantId(participantId, enrollmentId, isMentee)
  );

  const [settings, setSettings] = useReducer((
    state: MentorRequestModalSettings,
    newState: MentorRequestModalSettings,
  ) => ({ ...state, ...newState }), {
    source,
  });

  const dataQATag = (
    config.pendo.athena.mentorshipProgram.participantHome.mentorRequest.modal.closeButton
  );

  const removeParams = useClearUrlParams(MENTOR_REQUESTS_PARAMS);
  const openDiscardChangesDialog = useDiscardMentorMessage();

  const handleUnavailableRequest = useCallback(() => {
    dispatch(
      addAlertMessage({
        type: AlertMessageType.INFO,
        header: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTEE_VIEW.REQUEST_NOT_AVAILABLE.TITLE(),
        message: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTEE_VIEW.REQUEST_NOT_AVAILABLE.BODY(),
      }),
    );
    removeParams();
  }, [dispatch, removeParams]);

  const onClose = useCallback(() => {
    if (isMentor || mentorRequestContentRef.current?.canDiscardChanges) {
      removeParams();
    } else {
      openDiscardChangesDialog(removeParams);
    }
  }, [removeParams, isMentor]);

  const navigate = useCallback((action: () => void) => {
    if (mentorRequestContentRef.current?.canDiscardChanges) {
      action();
    } else {
      openDiscardChangesDialog(action);
    }
  }, [openDiscardChangesDialog]);

  const redirectToMentoringProgramRoot = useCallback((params: object) => {
    navigate(() => {
      mentorRequestContentRef.current?.resetForm?.();
      $state.go('mentoring-program-root', params, {
        notify: false,
      });
    });
  }, [$state, navigate]);

  const navigateToMentorRequest = useCallback((index: number) => {
    const request = mentoringProgramRequests[index];
    dispatch(setMentorRequestCarouselIndex(index));
    redirectToMentoringProgramRoot({
      mentorRequest: request.id,
      mentorId: undefined,
      source: MentorRequestSource.MENTEE_HOME,
    });
  }, [mentoringProgramRequests.length, dispatch, redirectToMentoringProgramRoot]);

  const navigateToMentorById = useCallback((mentorId: number | undefined) => {
    redirectToMentoringProgramRoot({
      source: MentorRequestSource.FIND_MENTOR,
      mentorId,
      mentorRequest: undefined,
    });
  }, [redirectToMentoringProgramRoot]);

  const getMentorRequestByIndex = useCallback((index: number, exists: boolean) => {
    if (!exists) return;
    navigateToMentorRequest(index);
  }, [navigateToMentorRequest]);

  const getMentorRequestById = useCallback((mentorId: number | undefined) => {
    if (!mentorId) return;
    navigateToMentorById(mentorId);
  }, [navigateToMentorById]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }
    if (e.key === 'ArrowLeft' && settings.hasLeftItems) {
      settings.getPreviousMentorRequest();
    }
    if (e.key === 'ArrowRight' && settings.hasRightItems) {
      settings.getNextMentorRequest();
    }
  };

  const handleApprovedRequest = useCallback(() => {
    const { menteeEnrollmentId, mentorEnrollmentId } = mentoringProgramRequest;
    if (menteeEnrollmentId === enrollmentId || mentorEnrollmentId === enrollmentId) {
      const { assignedConnections = [] } = mentorshipProgramEnrollment;
      const assignedConnection = assignedConnections.find(({ assignedEnrollment }) => (
        assignedEnrollment.id === menteeEnrollmentId || assignedEnrollment.id === mentorEnrollmentId
      ));

      const connectionId = assignedConnection?.connectionId;
      if (connectionId) {
        $state.go('mentoring-program-single-connection', {
          connectionId,
          showProfile: undefined,
          mentorRequest: undefined,
          mentorId: undefined,
          source: undefined,
        }, {
          reload: false,
        });
      }
    }
  }, [mentoringProgramRequest, enrollmentId, mentorshipProgramEnrollment]);

  useEffect(() => {
    if (mentoringProgramRequests.length && typeof carouselIndex === 'number') {
      const previousIndex = carouselIndex - 1;
      const nextIndex = carouselIndex + 1;
      const hasLeftItems = previousIndex >= 0;
      const hasRightItems = nextIndex < mentoringProgramRequests.length;

      setSettings({
        hasLeftItems,
        hasRightItems,
        getPreviousMentorRequest: (
          getMentorRequestByIndex.bind(null, previousIndex, hasLeftItems)
        ),
        getNextMentorRequest: (
          getMentorRequestByIndex.bind(null, nextIndex, hasRightItems)
        ),
      });
    }
  }, [mentoringProgramRequests.length, carouselIndex]);

  useEffect(() => {
    if (!mentoringProgramRequest) return;
    const { state } = mentoringProgramRequest;

    if (state === SingleMentorRequestStatus.APPROVED) {
      handleApprovedRequest();
    } else if (
      state === SingleMentorRequestStatus.REJECTED
      || state === SingleMentorRequestStatus.DROPPED
      || state === SingleMentorRequestStatus.WITHDRAW
    ) {
      handleUnavailableRequest();
    }
  }, [mentoringProgramRequest]);

  useEffect(() => {
    if (source && (requestId || mentorId)) {
      setSettings({
        showGoBack: source === MentorRequestSource.FIND_MENTOR,
        showCarousel: (
          source === MentorRequestSource.FIND_MENTOR
          || source === MentorRequestSource.MENTEE_HOME
        ),
      });
      if (mentorId) {
        setSettings({
          participant: {
            ...mentoringProgramParticipant,
            isAvailable: mentoringProgramParticipant?.availability > 0,
          },
          hasLeftItems: !!previousMentorId,
          hasRightItems: !!nextMentorId,
          getPreviousMentorRequest: getMentorRequestById.bind(null, previousMentorId),
          getNextMentorRequest: getMentorRequestById.bind(null, nextMentorId),
        });
      } else if (requestId) {
        setSettings({
          request: mentoringProgramRequest,
        });
      }
    }
  }, [source, requestId, mentorId]);

  useEffect(() => {
    if (!Number(requestId)) return;
    if (!mentoringProgramRequest) {
      wrapThunkAction(
        dispatch(
          getMentorRequest({
            mentorshipProgramId: programId,
            mentorRequestId: Number(requestId),
          }),
        ),
      ).catch(handleUnavailableRequest);
    }
  }, [requestId]);

  useEffect(() => {
    if (participantId && !participantEnrollment) {
      wrapThunkAction(
        dispatch(getEnrollment({
          programId: programId,
          enrollmentId: participantId,
        })),
      ).catch(handleUnavailableRequest);
    }
  }, [participantId]);

  useEffect(() => {
    if (mentoringProgramRequest) {
      setSettings({
        request: mentoringProgramRequest,
      });
    }
  }, [mentoringProgramRequest]);

  useEffect(() => {
    if (participantEnrollment?.id !== settings.participant?.id) {
      setSettings({
        participant: {
          ...mentoringProgramParticipant,
          ...participantEnrollment,
        },
      });
    }
  }, [participantEnrollment, mentoringProgramParticipant]);

  useEffect(() => {
    if (source === MentorRequestSource.FIND_MENTOR || source === MentorRequestSource.MENTEE_HOME) {
      setSettings({
        request: requests.at(-1),
        requests: getTimelineRequests(requests),
      });
    }
  }, [mentorId, source, requests?.[0]?.id]);

  useEffect(() => {
    if (shouldLoad && searchParams && source === MentorRequestSource.FIND_MENTOR) {
      const requestedPage = searchParams.page;
      if (lastRequestedPageRef.current !== requestedPage) {
        lastRequestedPageRef.current = requestedPage;
        dispatch(searchMentors({
          programId,
          ...searchParams,
          pageSize: PAGE_SIZE,
        }));
      }
    }
  }, [shouldLoad, searchParams, source, programId, dispatch]);

  useEffect(() => {
    if (mentoringProgramRequest) {
      const isUserMentor = mentoringProgramRequest.mentorEnrollmentId === enrollmentId;
      const isUserMentee = mentoringProgramRequest.menteeEnrollmentId === enrollmentId;
      if (!isUserMentor && !isUserMentee) {
        onClose();
        AlertMessages.error(
          'FORM.OOPS',
          'FORM.SOMETHING_WRONG',
        );
      }
    }
  }, [mentoringProgramRequest, participantId]);

  useEffect(() => {
    if (isMentor && availability <= 0) {
      onClose();
    }
  }, [availability, isMentor]);

  const isRequestLoading = Number(requestId) && !mentoringProgramRequest;
  const isParticipantLoading = participantId && !participantEnrollment;
  const isRequestAvailable = mentoringProgramRequest && (
    mentoringProgramRequest.state !== SingleMentorRequestStatus.REJECTED
    && mentoringProgramRequest.state !== SingleMentorRequestStatus.DROPPED
    && mentoringProgramRequest.state !== SingleMentorRequestStatus.WITHDRAW
  );
  const isRequestUnavailable = mentoringProgramRequest && !isRequestAvailable;

  if (isRequestUnavailable || (availability <= 0 && isMentor)) {
    return null;
  }

  const isLoading = isRequestLoading || isParticipantLoading;

  return (
    <div onKeyDown={handleKeyDown}>
      <NvModal
        doubleModal
        show
        onClose={onClose}
        fullHeight={false}
        modalStyles={modalStyles}
        type={ModalType.NO_DIALOG}
        closeOnBackdropClick
        body={(
          <MentorRequestContent
            ref={mentorRequestContentRef}
            {...settings}
            isLoading={isLoading}
          />
        )}
        closeDataQa={dataQATag}
      />
    </div>
  );
};

export default MentorRequestModal;
